/**
 * ตัวเดิน EMVCo BER-TLV — ฟังก์ชันบริสุทธิ์ ไม่มี dependency
 *
 * รูปแบบ: `TT LL VVVV...`  tag 2 อักขระ · length 2 หลักฐานสิบ · value ยาวเท่า length
 * ใช้ทั้งกับ QR ชั้นนอกและ template ซ้อนใน (tag 26–51 ของ EMVCo)
 */

export interface TlvNode {
  tag: string;
  /** ค่าดิบ ไม่ trim ไม่แปลง */
  value: string;
  /** ตำแหน่งเริ่มของ value ใน payload ตั้งต้น ใช้ยืนยันว่าอ่านตรงตัว */
  offset: number;
  /** template ซ้อนใน ถ้า value เป็น TLV ที่ถูกต้องทั้งก้อน */
  children?: TlvNode[];
}

const isDigits = (s: string): boolean => /^[0-9]+$/.test(s);

/**
 * เดิน TLV หนึ่งชั้น
 * คืน `null` เมื่อสตริงไม่ใช่ TLV ที่ถูกต้อง (ความยาวไม่ลงตัว / len ไม่ใช่ตัวเลข)
 * ยอมให้มี whitespace ต่อท้ายได้ เพราะเครื่องอ่าน QR บางตัวเติม \n มา
 */
export function parseTlv(payload: string, baseOffset = 0): TlvNode[] | null {
  const s = payload.replace(/\s+$/, "");
  if (s.length < 4) return null;

  const out: TlvNode[] = [];
  let i = 0;
  while (i < s.length) {
    if (i + 4 > s.length) return null;
    const tag = s.slice(i, i + 2);
    const lenStr = s.slice(i + 2, i + 4);
    if (!isDigits(tag) || !isDigits(lenStr)) return null;
    const len = Number(lenStr);
    const start = i + 4;
    const end = start + len;
    if (end > s.length) return null;
    out.push({ tag, value: s.slice(start, end), offset: baseOffset + start });
    i = end;
  }
  return out.length > 0 ? out : null;
}

/**
 * tag ที่ value ข้างในเป็น TLV อีกชั้น
 *   00     template ของ **QR ตรวจสอบสลิป** ที่ธนาคารไทยพิมพ์บนสลิปจริง — เนื้อทั้งหมด
 *          (00.00 ตัวระบุรูปแบบ · 00.01 รหัสธนาคาร · 00.02 เลขอ้างอิง) อยู่ในนี้ ดู FORMATS.md หัวข้อ 1
 *   26–51  Merchant Account Information (EMVCo)
 *   62     Additional Data Field Template (เลขอ้างอิงของสลิปมักอยู่ที่ 62.05)
 *   64     Merchant Information — Language Template
 *   80–99  unreserved templates
 *
 * จำกัดการกางชั้นไว้แค่ tag เหล่านี้ เพราะเลขอ้างอิงล้วนๆ บางตัวบังเอิญ "อ่านเป็น TLV ได้"
 * ถ้ากางมั่วจะได้ `trans_ref` ที่ถูกตัดหัวไปสองสี่ตัว → `ref_hash` ผิด → กันสลิปซ้ำพัง
 *
 * tag `00` ปลอดภัยที่จะกาง เพราะ `parseTlvDeep()` ผูก `children` ให้ก็ต่อเมื่อ value ทั้งก้อน
 * เป็น TLV ที่ถูกต้องเท่านั้น — ของ PromptPay payment QR ที่ tag `00` เก็บเลขเวอร์ชัน `"01"`
 * (ยาว 2) จึงไม่ถูกกางอยู่แล้ว และค่าของ tag `00` ตัวเดิมก็ยังอยู่ครบไม่ถูกแตะ
 */
export function isTemplateTag(tag: string): boolean {
  const n = Number(tag);
  if (!Number.isInteger(n)) return false;
  return n === 0 || (n >= 26 && n <= 51) || n === 62 || n === 64 || (n >= 80 && n <= 99);
}

/** เดิน TLV แบบซ้อนชั้น — กางเฉพาะ tag ที่เป็น template ตามสเปก */
export function parseTlvDeep(payload: string, depth = 0, baseOffset = 0): TlvNode[] | null {
  const nodes = parseTlv(payload, baseOffset);
  if (!nodes) return null;
  if (depth >= 2) return nodes;
  for (const n of nodes) {
    if (!isTemplateTag(n.tag)) continue;
    if (n.value.length < 4) continue;
    const kids = parseTlvDeep(n.value, depth + 1, n.offset);
    if (kids) n.children = kids;
  }
  return nodes;
}

/** ยุบต้นไม้ TLV เป็นรายการแบน tag เป็น path เช่น `29.01` */
export function flattenTlv(nodes: TlvNode[], prefix = ""): Array<TlvNode & { path: string }> {
  const out: Array<TlvNode & { path: string }> = [];
  for (const n of nodes) {
    const path = prefix ? `${prefix}.${n.tag}` : n.tag;
    out.push({ ...n, path });
    if (n.children) out.push(...flattenTlv(n.children, path));
  }
  return out;
}

/** พิมพ์ต้นไม้ TLV ให้อ่านด้วยตา — ใช้ตอนสำรวจสลิปใบใหม่ (`qr:report --dump`) */
export function formatTlvTree(nodes: TlvNode[], indent = 0): string {
  const pad = "  ".repeat(indent);
  return nodes
    .map((n) => {
      const head = `${pad}${n.tag} (${String(n.value.length).padStart(2, "0")}) ${JSON.stringify(n.value)}`;
      return n.children ? `${head}\n${formatTlvTree(n.children, indent + 1)}` : head;
    })
    .join("\n");
}

/**
 * ประกอบ TLV หนึ่งฟิลด์ — ใช้ในเทสต์ (round-trip กับ `parseTlv`) และตัวสร้างภาพตัวอย่าง
 * โยน error เมื่อ value ยาวเกิน 99 อักขระ เพราะ EMVCo ใช้ความยาว 2 หลัก
 */
export function tlv(tag: string, value: string): string {
  if (!/^[0-9]{2}$/.test(tag)) throw new Error(`tag ต้องเป็นเลขสองหลัก: ${tag}`);
  if (value.length > 99) throw new Error(`value ยาวเกิน 99 อักขระ: ${value.length}`);
  return tag + String(value.length).padStart(2, "0") + value;
}
